import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.css';
import { useWrapperMessage } from './Hooks/useWrapperMessage';
import { Booking, Lesson, Order, SCORMContentBlock, ScormDataModelData } from '@shared/types';
import classNames from 'classnames';
import { FullscreenIcon, Spinner } from 'evergreen-ui';
// @ts-expect-error Cademy Shared Web is untyped
import { request, toaster } from 'cademy-shared-web';
import { CademyError } from '@shared/domain-shared';
import Bugsnag from '@bugsnag/js';
import { useDebouncedAsyncCallback } from './Hooks/useDebouncedAsyncCallback';
import { Button } from 'components/_ui/Buttons';
import { Environment } from 'services/Environment';

const getSCORMDataModel = async (
    entityId: Order['_id'] | Booking['_id'],
    lessonId: Lesson['_id'],
    blockId: SCORMContentBlock['_id']
) => {
    const { data } = await request.get(
        `/scorm/runtime/${entityId}/${lessonId}/${blockId}/initialise`
    );
    return data;
};

const saveSCORMDataModel = async (
    entityId: Order['_id'] | Booking['_id'],
    lessonId: Lesson['_id'],
    blockId: SCORMContentBlock['_id'],
    model: ScormDataModelData
): Promise<void> => {
    await request.post(`/scorm/runtime/${entityId}/${lessonId}/${blockId}`, { dataModels: model });
};

export type SCORMContentsProps = {
    entityId: Order['_id'] | Booking['_id'];
    lessonId: Lesson['_id'];
    blockId: SCORMContentBlock['_id'];
    entryUrl: string;
    onLessonStatusChanged: () => void;
};

export const SCORMContents: FunctionComponent<SCORMContentsProps> = ({
    entityId,
    lessonId,
    blockId,
    entryUrl,
    onLessonStatusChanged,
}) => {
    const frameRef = useRef<HTMLIFrameElement | null>(null);
    const [frameWindow, setFrameWindow] = useState<Window | null>(null);
    const [initialised, setInitialised] = useState<boolean>(false);

    const isLoading = useMemo(() => {
        return initialised === false;
    }, [initialised]);

    const bus = useWrapperMessage({
        foreignWindow: frameWindow,
        foreignOrigin: Environment.SCORM_WRAPPER_URL || '',
        instanceId: blockId,
    });

    useEffect(() => {
        if (!frameRef.current) {
            return;
        }
        if (!frameRef.current.contentWindow) {
            return;
        }
        setFrameWindow(frameRef.current.contentWindow);
    }, [frameRef]);

    useEffect(() => {
        bus.listen();
    }, [bus]);

    const initialise = useCallback(async () => {
        const dataModel = await getSCORMDataModel(entityId, lessonId, blockId);

        bus.emit('wrapper:initial-data', {
            dataModels: dataModel.dataModels,
            scormPackageUrl: entryUrl,
            token: dataModel.token,
        });
    }, [bus, entityId, lessonId, blockId, entryUrl]);

    useEffect(() => {
        return bus.on('wrapper:loaded', initialise);
    }, [bus, initialise]);

    useEffect(() => {
        return bus.on('wrapper:initialized', () => setInitialised(true));
    }, [setInitialised, bus]);

    const debouncedSaveSCORMDataModel = useDebouncedAsyncCallback(saveSCORMDataModel);
    useEffect(() => {
        return bus.on('wrapper:datamodel-updated', (model) => {
            debouncedSaveSCORMDataModel(entityId, lessonId, blockId, model);
        });
    }, [bus, debouncedSaveSCORMDataModel, entityId, lessonId, blockId]);

    useEffect(() => {
        return bus.on('wrapper:error', (error) => {
            toaster.danger(
                'There was an error delivering this lesson content.  Contact the course provider if the issue persists.'
            );
            const cademyError = CademyError.fromUnknown(error);
            console.error(cademyError);
            Bugsnag.notify(cademyError);
        });
    }, [bus]);

    useEffect(() => {
        return bus.on('SCORM:lesson-status-changed', onLessonStatusChanged);
    }, [bus, onLessonStatusChanged]);

    const handleFullScreen = useCallback(() => {
        if (!frameRef.current) {
            return;
        }
        frameRef.current.requestFullscreen();
    }, [frameRef]);

    const renderFullScreenButton = useMemo(() => {
        if (isLoading) return null;
        const isIPhone = /iPhone/i.test(navigator.userAgent);
        if (!window.document.fullscreenEnabled || isIPhone) return null; //Fullscreen API not supported
        return (
            <Button
                onClick={handleFullScreen}
                className={styles.fullScreenBtn}
                appearance="primary"
                size="sm"
            >
                <FullscreenIcon />
                Full Screen
            </Button>
        );
    }, [isLoading, handleFullScreen]);

    return (
        <div className={styles.wrapper}>
            {renderFullScreenButton}
            <div className={styles.container}>
                <iframe
                    title="SCORM"
                    className={classNames(styles.iframe, { [styles.loading]: isLoading })}
                    src={`${Environment.SCORM_WRAPPER_URL}?instanceId=${encodeURIComponent(blockId)}&parentOrigin=${encodeURIComponent(window.location.origin)}`}
                    ref={frameRef}
                />
                {isLoading ? <Spinner /> : null}
            </div>
        </div>
    );
};
